import React from 'react';
import { motion } from 'framer-motion';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  text?: string;
  className?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  size = 'md', 
  text = 'Loading quantum particles...',
  className = ''
}) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16'
  };

  const textSizes = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg'
  };

  return (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      <div className="relative">
        {/* Outer ring */}
        <motion.div
          className={`${sizeClasses[size]} border-4 border-purple-500/30 rounded-full`}
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        />
        
        {/* Inner quantum particles */}
        <motion.div
          className={`${sizeClasses[size]} absolute inset-0 border-4 border-transparent border-t-purple-400 rounded-full`}
          animate={{ rotate: -360 }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
        />
        
        {/* Center quantum core */}
        <motion.div
          className={`${size === 'sm' ? 'w-2 h-2' : size === 'md' ? 'w-3 h-3' : 'w-4 h-4'} bg-gradient-to-r from-purple-400 to-pink-400 rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2`}
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.7, 1, 0.7]
          }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        />
        
        {/* Orbiting particles */}
        {size !== 'sm' && (
          <>
            <motion.div
              className="w-1 h-1 bg-yellow-400 rounded-full absolute top-0 left-1/2 transform -translate-x-1/2"
              animate={{ 
                rotate: 360,
                scale: [1, 1.5, 1]
              }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            />
            <motion.div
              className="w-1 h-1 bg-blue-400 rounded-full absolute bottom-0 left-1/2 transform -translate-x-1/2"
              animate={{ 
                rotate: -360,
                scale: [1, 1.5, 1]
              }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
            />
          </>
        )}
      </div>
      
      {text && (
        <motion.p
          className={`${textSizes[size]} text-gray-300 mt-4 text-center max-w-xs`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {text}
        </motion.p>
      )}
    </div>
  );
};

// Specialized loading components
export const QuantumLoading: React.FC<{ text?: string }> = ({ text }) => (
  <LoadingSpinner 
    size="lg" 
    text={text || "Consulting the quantum realm..."} 
    className="min-h-[200px]"
  />
);

export const TaskLoading: React.FC = () => (
  <LoadingSpinner 
    size="sm" 
    text="Collapsing quantum state..." 
    className="py-4"
  />
);

export const UniverseLoading: React.FC = () => (
  <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white flex items-center justify-center">
    <LoadingSpinner 
      size="lg" 
      text="Initializing the multiverse..." 
    />
  </div>
);
